import React, { useState } from 'react';
import {
  useForm,
  Controller,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  TextField,
  SelectionControlLabel,
  Checkbox,
  LoadingButton,
} from '@one-thd/sui-atomic-components';
import { addressesApi } from '../../../api/common/addresses';
import { TB2BRequestAddressData } from '../../../models/b2b/addresses';
import { TB2CRequestAddressData } from '../../../models/b2c/addresses';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import { TErrorMessage } from '../../../models/common/alert-messages';
import { Alert } from '../../shared/alert/alert';
import {
  mapAddAddressToRequestModel,
  mapAddressFormToMappedModal,
  mapEditAddressToRequestModel,
} from '../../../utils/mapping-addresses';
import { getTextFieldStatus, getAddressFormValues } from '../../../utils/form';
import { PHONE_PLACEHOLDER } from '../../../constants/placeholders';
import { useAddresses } from '../../../hooks/use-addresses';
import { FormTitle } from '../../shared/form-title/form-title';
import {
  FORM_MODE,
  IMappedAddress,
  TAddressFormValues,
} from '../../../models/common/addresses';
import { validationSchema } from './validation';
import { getHyphenatedPhoneNumber } from '../../../utils/general';
import { TextButton } from '../../shared/text-button/text-button';
import { DefaultAddressModal } from '../default-address-modal/default-address-modal';
import { useModal } from '../../../hooks/use-modal';
import {
  setAddPhoneLinkAnalytics,
  setAddEditAddressAnalytics,
} from '../../../utils/address-analytics';
import { pushErrorEvent } from '../../../utils/analytics';
import { AddressFields } from '../../shared/address/address-fields';

interface IAddressFormProps {
  onAddSuccess: () => void;
  onEditSuccess: (adrId: number) => void;
}

export const AddressForm = ({
  onAddSuccess,
  onEditSuccess,
}: IAddressFormProps) => {
  const {
    isB2B,
    addresses,
    addressFormMode,
    editableAddress,
    setAddressFormMode,
    setEditableAddress,
    changeDefault,
  } = useAddresses();

  const { openModal, hideModal, setComponentProps } = useModal();

  const isAddAddressForm = addressFormMode === FORM_MODE.ADD;
  const formTitleText = isAddAddressForm ? 'Add Address' : 'Edit Address';
  const defaultValues = getAddressFormValues(editableAddress);

  const [isAlternatePhoneEnabled, setAlternatePhoneField] = useState(
    !!defaultValues.alternatePhone || false,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<TErrorMessage | null>(null);

  const formMethods = useForm<TAddressFormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const {
    control,
    formState: { errors: formErrors, isValid, dirtyFields },
    handleSubmit,
    getValues,
  } = formMethods;

  const { isZipCodePending, isZipCodeApiError } = getValues();

  const isNoAddresses = !Number(
    addresses?.paginationInfo?.totalNumberOfRecords,
  );

  const resetAddressForm = () => {
    setAddressFormMode(null);
    setEditableAddress(null);
  };

  const handleOnClickAlternatePhone = () => {
    setAlternatePhoneField(true);
    setAddPhoneLinkAnalytics(isAddAddressForm);
  };

  const isFormDirty = () =>
    Object.values(dirtyFields).some((value) => {
      return value || false;
    });

  const executeUpdateAddress = (
    updateAddressRequest: TB2BRequestAddressData | TB2CRequestAddressData,
    editableAdr: IMappedAddress,
    isDefault: boolean,
  ) => {
    const handleSuccessEditResponse = () => {
      onEditSuccess(editableAdr.addrIdentifier);
      setAddEditAddressAnalytics(
        isAddAddressForm,
        updateAddressRequest?.isDefault,
      );
    };

    const handleErrorEditResponse = () => {
      setError(ERR_UNABLE_TO_SAVE);

      if (isDefault) {
        pushErrorEvent(
          'address card: set as default',
          'inline',
          'n/a',
          ERR_UNABLE_TO_SAVE.message,
        );
        return;
      }

      pushErrorEvent(
        'address card: edit address',
        'inline',
        'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };

    if (!isB2B && isDefault && !defaultValues.isDefault) {
      return changeDefault(null, updateAddressRequest as TB2CRequestAddressData)
        .then(() => {
          handleSuccessEditResponse();
        })
        .catch(() => {
          handleErrorEditResponse();
        });
    }

    return addressesApi
      .editAddress(isB2B, updateAddressRequest)
      .then(() => {
        handleSuccessEditResponse();
      })
      .catch(() => {
        handleErrorEditResponse();
      });
  };

  const executeAddAddress = (
    requestData: TB2BRequestAddressData | TB2CRequestAddressData,
    isDefault: boolean,
  ) => {
    return addressesApi
      .addNewAddress(isB2B, requestData)
      .then(async ({ data }) => {
        if (!isB2B && isDefault && !isNoAddresses) {
          const newAdr = data.addresses.address[0];
          await changeDefault(null, newAdr);
        }
        setAddEditAddressAnalytics(isAddAddressForm, isDefault);
        onAddSuccess();
      })
      .catch(() => {
        setError(ERR_UNABLE_TO_SAVE);
        pushErrorEvent(
          'address card: add address',
          'inline',
          'n/a',
          ERR_UNABLE_TO_SAVE.message,
        );
      });
  };

  const onSubmitForm: SubmitHandler<TAddressFormValues> = (values) => {
    setError(null);

    if (!isFormDirty()) {
      resetAddressForm();
      return;
    }

    const isFormValid = !isZipCodePending || !isZipCodeApiError || isValid;
    if (isFormValid) {
      if (isAddAddressForm) {
        const requestData = mapAddAddressToRequestModel(
          isB2B,
          values,
          isNoAddresses,
        );

        if (values?.isDefault && !isNoAddresses) {
          const mappedFormValues = mapAddressFormToMappedModal(values);

          openModal(DefaultAddressModal, {
            address: mappedFormValues,
            onSave: () => {
              setComponentProps({ isLoading: true });
              executeAddAddress(requestData, true).finally(() => {
                setComponentProps({ isLoading: false });
                hideModal();
              });
            },
            isLoading: false,
          });
        } else {
          setLoading(true);
          executeAddAddress(requestData, values.isDefault).finally(() => {
            setLoading(false);
          });
        }
      }
      if (!isAddAddressForm && addresses && editableAddress) {
        const addressDataFromResponse = addresses.response.find(
          (adr) => adr.addrIdentifier === editableAddress?.addrIdentifier,
        );
        if (!addressDataFromResponse) {
          throw Error('Editable address not found in addresses list');
        }
        const updateAddressRequest = mapEditAddressToRequestModel(
          isB2B,
          values,
          addressDataFromResponse,
        );
        if (values?.isDefault && !defaultValues.isDefault) {
          const mappedFormValues = mapAddressFormToMappedModal(values);
          openModal(DefaultAddressModal, {
            address: mappedFormValues,
            onSave: async () => {
              setComponentProps({ isLoading: true });
              await executeUpdateAddress(
                updateAddressRequest,
                editableAddress,
                values.isDefault,
              ).finally(() => {
                hideModal();
              });
            },
            isLoading: false,
          });
        } else {
          setLoading(true);
          executeUpdateAddress(
            updateAddressRequest,
            editableAddress,
            values.isDefault,
          ).finally(() => {
            setLoading(false);
          });
        }
      }
    }
  };

  return (
    <div className="lg:sui-max-w-[437px]">
      <FormTitle
        title={formTitleText}
        actionLabel="Cancel"
        onActionClick={resetAddressForm}
      />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmitForm)} className="sui-pt-8">
          <div className="sui-grid sui-gap-y-5 sui-mb-4">
            <AddressFields>
              {isB2B && (
                <Controller
                  name="nickName"
                  control={control}
                  render={({ field }) => {
                    const { ref, value, name, onChange, ...rest } = field;
                    return (
                      <TextField
                        label="Address Nickname"
                        status={getTextFieldStatus(formErrors.nickName)}
                        statusMessage={formErrors.nickName?.message}
                        optional
                        value={value || ''}
                        name={name}
                        onChange={onChange}
                        InputProps={{
                          inputProps: {
                            ...rest,
                            'data-qm-mask': '',
                            'data-testid': 'addressNicknameField',
                          },
                        }}
                      />
                    );
                  }}
                />
              )}
              {isB2B && (
                <Controller
                  name="companyName"
                  control={control}
                  render={({ field }) => {
                    const { ref, value, name, onChange, ...rest } = field;
                    return (
                      <TextField
                        label="Company Name"
                        status={getTextFieldStatus(formErrors.companyName)}
                        statusMessage={formErrors.companyName?.message}
                        optional
                        value={value || ''}
                        name={name}
                        onChange={onChange}
                        InputProps={{
                          inputProps: {
                            ...rest,
                            'data-testid': 'company-name-add-address',
                          },
                        }}
                      />
                    );
                  }}
                />
              )}
            </AddressFields>
            {isAlternatePhoneEnabled && (
              <Controller
                name="alternatePhone"
                control={control}
                render={({ field }) => {
                  const { ref, value, name, onChange, ...rest } = field;
                  return (
                    <TextField
                      label="Alternate Phone"
                      optional
                      status={getTextFieldStatus(formErrors.alternatePhone)}
                      statusMessage={formErrors.alternatePhone?.message}
                      placeholder={PHONE_PLACEHOLDER}
                      value={value || ''}
                      name={name}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        onChange(
                          getHyphenatedPhoneNumber(
                            event.currentTarget.value || '',
                          ),
                        );
                      }}
                      InputProps={{
                        inputProps: {
                          ...rest,
                          'data-qm-mask': '',
                          'data-testid': 'alternate-phone-add-address',
                        },
                      }}
                    />
                  );
                }}
              />
            )}
          </div>
          {!isAlternatePhoneEnabled && (
            <TextButton
              className="sui-py-4"
              onClick={handleOnClickAlternatePhone}
              dataTestId="add-another-phone-add-address"
            >
              Add Another Phone
            </TextButton>
          )}
          <div className="sui-mb-8 sui-pt-4">
            <SelectionControlLabel label="Make this my default address">
              <Controller
                name="isDefault"
                control={control}
                render={({ field }) => {
                  const { onChange, onBlur, name, ref } = field;
                  return (
                    <Checkbox
                      onChange={onChange}
                      onBlur={onBlur}
                      name={name}
                      ref={ref}
                      checked={isNoAddresses || field.value}
                      disabled={isNoAddresses || !!editableAddress?.isDefault}
                      data-testid="make-address-default-checkbox"
                    />
                  );
                }}
              />
            </SelectionControlLabel>
          </div>
          {error && (
            <div className="sui-mb-8">
              <Alert status="error">{error.message}</Alert>
            </div>
          )}
          <div className="sui-grid sui-grid-cols-1 sui-gap-4 sm:sui-grid-cols-2">
            <div className="sui-order-last sm:sui-order-first">
              <Button
                fullWidth
                variant="secondary"
                onClick={resetAddressForm}
                data-testid="cancel-add-address-btn"
              >
                Cancel
              </Button>
            </div>
            <LoadingButton
              loading={loading}
              fullWidth
              variant="primary"
              type="submit"
              data-testid="save-changes-add-address-btn"
            >
              Save Changes
            </LoadingButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
